/**
 * AdjustmentsPage — Issue #329.
 *
 * 财务调整请求审批与执行页面：
 *   - 列表（按状态/类型过滤）
 *   - 创建（refund / make_up_price / transfer_project）
 *   - 提交、审核（approve/reject/request_changes）、执行、取消
 *
 * 所有写操作经 @jinzhuan/api-client 走真实后端；
 * 执行成功会生成不可变 PaymentRecord 凭证并重新计算解锁状态。
 */
import { useEffect, useMemo, useState } from 'react';
import Decimal from 'decimal.js';
import { useAuth } from '@/hooks/useAuth';
import {
  FINANCE_PERMISSIONS,
  hasPermission,
} from '@/lib/financePermissions';
import {
  useFinanceAdjustments,
  useFinanceAdjustmentActions,
  useFinanceAdjustmentDetail,
  type CreateFinanceAdjustmentDto,
  type FinanceAdjustmentSummaryDto,
} from '@/hooks/useFinanceExtended';

const STATUS_LABELS: Record<string, string> = {
  draft: '草稿',
  submitted: '已提交',
  changes_requested: '需修改',
  approved: '已审批',
  rejected: '已驳回',
  executed: '已执行',
  cancelled: '已取消',
};

const STATUS_COLORS: Record<string, string> = {
  draft: 'bg-gray-100 text-gray-700',
  submitted: 'bg-yellow-100 text-yellow-800',
  changes_requested: 'bg-amber-100 text-amber-800',
  approved: 'bg-blue-100 text-blue-800',
  rejected: 'bg-red-100 text-red-700',
  executed: 'bg-green-100 text-green-800',
  cancelled: 'bg-gray-200 text-gray-500',
};

const TYPE_LABELS: Record<string, string> = {
  refund: '退款',
  make_up_price: '补差价',
  transfer_project: '转项目',
};

function fmtCny(amount: string): string {
  return `¥${new Decimal(amount).toFixed(2)}`;
}

function StatusBadge({ status }: { status: string }) {
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
        STATUS_COLORS[status] ?? 'bg-gray-100 text-gray-600'
      }`}
    >
      {STATUS_LABELS[status] ?? status}
    </span>
  );
}

// ─── Create dialog ────────────────────────────────────────────────────────────

interface CreateDialogProps {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
}

function CreateAdjustmentDialog({ open, onClose, onCreated }: CreateDialogProps) {
  const { create, loading, error } = useFinanceAdjustmentActions();
  const [form, setForm] = useState<CreateFinanceAdjustmentDto>({
    adjustmentType: 'refund',
    customerId: '',
    title: '',
    reason: '',
    amount: '',
    currency: 'CNY',
  });

  if (!open) return null;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const result = await create(form);
    if (result) {
      onCreated();
      onClose();
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="w-full max-w-lg rounded-lg bg-white p-6 shadow-xl">
        <h2 className="mb-4 text-lg font-semibold">新建调整请求</h2>
        {error && (
          <div className="mb-3 rounded bg-red-50 px-3 py-2 text-sm text-red-700">
            {error.message}
          </div>
        )}
        <form
          onSubmit={(e) => {
            void handleSubmit(e);
          }}
          className="space-y-3"
        >
          <div>
            <label className="block text-sm font-medium text-gray-700">调整类型</label>
            <select
              className="mt-1 w-full rounded border border-gray-300 px-3 py-1.5 text-sm"
              value={form.adjustmentType}
              onChange={(e) =>
                setForm({
                  ...form,
                  adjustmentType: e.target.value as CreateFinanceAdjustmentDto['adjustmentType'],
                })
              }
            >
              <option value="refund">退款</option>
              <option value="make_up_price">补差价</option>
              <option value="transfer_project">转项目</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">客户 ID</label>
            <input
              required
              className="mt-1 w-full rounded border border-gray-300 px-3 py-1.5 text-sm"
              value={form.customerId}
              onChange={(e) => setForm({ ...form, customerId: e.target.value })}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">标题</label>
            <input
              required
              className="mt-1 w-full rounded border border-gray-300 px-3 py-1.5 text-sm"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">金额（元，正数）</label>
            <input
              required
              type="text"
              inputMode="decimal"
              pattern="[0-9]+(\.[0-9]+)?"
              className="mt-1 w-full rounded border border-gray-300 px-3 py-1.5 text-sm"
              value={form.amount}
              onChange={(e) => setForm({ ...form, amount: e.target.value })}
            />
          </div>
          {form.adjustmentType === 'refund' && (
            <div>
              <label className="block text-sm font-medium text-gray-700">
                源收款记录 ID（必填）
              </label>
              <input
                required
                className="mt-1 w-full rounded border border-gray-300 px-3 py-1.5 text-sm"
                value={form.sourcePaymentRecordId ?? ''}
                onChange={(e) =>
                  setForm({
                    ...form,
                    sourcePaymentRecordId: e.target.value || undefined,
                  })
                }
              />
            </div>
          )}
          {(form.adjustmentType === 'transfer_project' ||
            form.adjustmentType === 'make_up_price') && (
            <div>
              <label className="block text-sm font-medium text-gray-700">
                目标订单项目 ID
              </label>
              <input
                required
                className="mt-1 w-full rounded border border-gray-300 px-3 py-1.5 text-sm"
                value={form.targetOrderProjectId ?? ''}
                onChange={(e) =>
                  setForm({
                    ...form,
                    targetOrderProjectId: e.target.value || undefined,
                  })
                }
              />
            </div>
          )}
          {form.adjustmentType === 'transfer_project' && (
            <div>
              <label className="block text-sm font-medium text-gray-700">
                源订单项目 ID（必填）
              </label>
              <input
                required
                className="mt-1 w-full rounded border border-gray-300 px-3 py-1.5 text-sm"
                value={form.sourceOrderProjectId ?? ''}
                onChange={(e) =>
                  setForm({
                    ...form,
                    sourceOrderProjectId: e.target.value || undefined,
                  })
                }
              />
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700">订单 ID（可选）</label>
            <input
              className="mt-1 w-full rounded border border-gray-300 px-3 py-1.5 text-sm"
              value={form.orderId ?? ''}
              onChange={(e) =>
                setForm({ ...form, orderId: e.target.value || undefined })
              }
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">
              特批参考 ID（可选）
            </label>
            <input
              className="mt-1 w-full rounded border border-gray-300 px-3 py-1.5 text-sm"
              value={form.specialApprovalRef ?? ''}
              onChange={(e) =>
                setForm({
                  ...form,
                  specialApprovalRef: e.target.value || undefined,
                })
              }
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">理由</label>
            <textarea
              required
              className="mt-1 w-full rounded border border-gray-300 px-3 py-1.5 text-sm"
              rows={3}
              value={form.reason}
              onChange={(e) => setForm({ ...form, reason: e.target.value })}
            />
          </div>
          <div className="mt-2 flex justify-end gap-2">
            <button
              type="button"
              className="rounded border border-gray-300 px-4 py-1.5 text-sm"
              onClick={onClose}
            >
              取消
            </button>
            <button
              type="submit"
              disabled={loading}
              className="rounded bg-primary px-4 py-1.5 text-sm font-medium text-primary-foreground disabled:opacity-50"
            >
              {loading ? '提交中…' : '创建草稿'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Detail panel ─────────────────────────────────────────────────────────────

interface DetailPanelProps {
  id: string | null;
  onChanged: () => void;
  onClose: () => void;
  permissions: readonly string[];
}

function DetailPanel({ id, onChanged, onClose, permissions }: DetailPanelProps) {
  const { data, refresh, error: detailError } = useFinanceAdjustmentDetail(id);
  const {
    submit,
    review,
    execute,
    cancel,
    loading,
    error: actionError,
  } = useFinanceAdjustmentActions();

  useEffect(() => {
    if (id) void refresh();
  }, [id, refresh]);

  if (!id) return null;
  if (!data) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
        <div className="rounded bg-white p-6 text-sm">
          {detailError ? detailError.message : '加载中…'}
          <div className="mt-3 text-right">
            <button onClick={onClose} className="rounded border px-3 py-1 text-sm">
              关闭
            </button>
          </div>
        </div>
      </div>
    );
  }

  const canCreate = hasPermission(permissions, FINANCE_PERMISSIONS.ADJUSTMENT_CREATE);
  const canReview = hasPermission(permissions, FINANCE_PERMISSIONS.ADJUSTMENT_REVIEW);
  const canExecute = hasPermission(permissions, FINANCE_PERMISSIONS.ADJUSTMENT_EXECUTE);

  async function onSubmit() {
    if (!data) return;
    const ok = await submit(data.id, { expectedVersion: data.version });
    if (ok) {
      void refresh();
      onChanged();
    }
  }
  async function onReview(decision: 'approve' | 'reject' | 'request_changes') {
    if (!data) return;
    const comment = window.prompt('审核意见（可选）') ?? undefined;
    const ok = await review(data.id, {
      expectedVersion: data.version,
      decision,
      reviewerComment: comment,
    });
    if (ok) {
      void refresh();
      onChanged();
    }
  }
  async function onExecute() {
    if (!data) return;
    const note = window.prompt('执行备注（可选，留空回车）') ?? undefined;
    if (!window.confirm('执行后将生成不可变 PaymentRecord 凭证并刷新解锁状态，确认继续吗？')) {
      return;
    }
    const ok = await execute(data.id, {
      expectedVersion: data.version,
      executionNote: note,
    });
    if (ok) {
      void refresh();
      onChanged();
    }
  }
  async function onCancel() {
    if (!data) return;
    const reason = window.prompt('请输入取消原因：');
    if (!reason) return;
    const ok = await cancel(data.id, {
      expectedVersion: data.version,
      cancelReason: reason,
    });
    if (ok) {
      void refresh();
      onChanged();
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-lg bg-white p-6 shadow-xl">
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-lg font-semibold">{data.title}</h2>
            <p className="mt-1 text-xs text-gray-500">
              单号 <span className="font-mono">{data.adjustmentNo}</span> · 类型{' '}
              {TYPE_LABELS[data.adjustmentType] ?? data.adjustmentType} ·{' '}
              <StatusBadge status={data.status} />
            </p>
          </div>
          <button onClick={onClose} className="rounded border px-2 py-1 text-xs">
            关闭
          </button>
        </div>
        {(actionError || detailError) && (
          <div className="mt-3 rounded bg-red-50 px-3 py-2 text-sm text-red-700">
            {(actionError ?? detailError)?.message}
          </div>
        )}

        <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
          <Field label="客户" value={data.customerId} />
          <Field label="订单" value={data.orderId} />
          <Field label="合同" value={data.contractId} />
          <Field label="金额" value={fmtCny(data.amount)} />
          <Field label="币种" value={data.currency} />
          <Field label="特批参考" value={data.specialApprovalRef} />
          <Field label="源收款" value={data.sourcePaymentRecordId} />
          <Field label="源项目" value={data.sourceOrderProjectId} />
          <Field label="目标项目" value={data.targetOrderProjectId} />
          <Field label="执行出账" value={data.executionOutflowPaymentId} />
          <Field label="执行入账" value={data.executionInflowPaymentId} />
          <Field label="版本" value={String(data.version)} />
        </div>

        <div className="mt-4">
          <h3 className="text-sm font-semibold text-gray-700">理由</h3>
          <p className="mt-1 whitespace-pre-line rounded border border-gray-200 bg-gray-50 p-2 text-sm">
            {data.reason}
          </p>
        </div>

        <div className="mt-4">
          <h3 className="text-sm font-semibold text-gray-700">审计轨迹</h3>
          <ol className="mt-1 space-y-1 text-xs text-gray-600">
            {data.reviews.map((r) => (
              <li key={r.id}>
                <span className="font-mono">
                  {new Date(r.reviewedAt).toLocaleString()}
                </span>{' '}
                · {r.fromStatus} → {r.toStatus} · {r.decision} · 操作人 {r.reviewedBy}
                {r.reviewerComment ? ` · ${r.reviewerComment}` : ''}
              </li>
            ))}
            {data.reviews.length === 0 && <li>暂无审计记录</li>}
          </ol>
        </div>

        <div className="mt-4 flex flex-wrap gap-2 border-t pt-3">
          {(data.status === 'draft' || data.status === 'changes_requested') &&
            canCreate && (
              <button
                disabled={loading}
                onClick={() => void onSubmit()}
                className="rounded border border-blue-300 bg-blue-50 px-3 py-1 text-sm text-blue-700"
              >
                提交审核
              </button>
            )}
          {data.status === 'submitted' && canReview && (
            <>
              <button
                disabled={loading}
                onClick={() => void onReview('approve')}
                className="rounded border border-green-300 bg-green-50 px-3 py-1 text-sm text-green-700"
              >
                批准
              </button>
              <button
                disabled={loading}
                onClick={() => void onReview('reject')}
                className="rounded border border-red-300 bg-red-50 px-3 py-1 text-sm text-red-700"
              >
                驳回
              </button>
              <button
                disabled={loading}
                onClick={() => void onReview('request_changes')}
                className="rounded border border-amber-300 bg-amber-50 px-3 py-1 text-sm text-amber-700"
              >
                需修改
              </button>
            </>
          )}
          {data.status === 'approved' && canExecute && (
            <button
              disabled={loading}
              onClick={() => void onExecute()}
              className="rounded border border-emerald-300 bg-emerald-50 px-3 py-1 text-sm text-emerald-700"
            >
              执行
            </button>
          )}
          {(data.status === 'draft' ||
            data.status === 'submitted' ||
            data.status === 'changes_requested' ||
            data.status === 'approved') &&
            canCreate && (
              <button
                disabled={loading}
                onClick={() => void onCancel()}
                className="rounded border border-gray-300 bg-white px-3 py-1 text-sm text-gray-700"
              >
                取消
              </button>
            )}
        </div>
      </div>
    </div>
  );
}

function Field({ label, value }: { label: string; value: string | null | undefined }) {
  return (
    <div>
      <div className="text-xs text-gray-500">{label}</div>
      <div className="font-mono text-xs text-gray-800 break-all">
        {value ?? '—'}
      </div>
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function AdjustmentsPage() {
  const auth = useAuth();
  const permissions = useMemo(() => auth.permissions ?? [], [auth.permissions]);
  const canRead = hasPermission(permissions, FINANCE_PERMISSIONS.ADJUSTMENT_READ);
  const canCreate = hasPermission(permissions, FINANCE_PERMISSIONS.ADJUSTMENT_CREATE);

  const [statusFilter, setStatusFilter] = useState<string>('');
  const [typeFilter, setTypeFilter] = useState<string>('');
  const [search, setSearch] = useState<string>('');
  const [createOpen, setCreateOpen] = useState(false);
  const [detailId, setDetailId] = useState<string | null>(null);

  const { data, loading, error, fetchList } = useFinanceAdjustments();

  const refresh = () =>
    fetchList({
      status: statusFilter || undefined,
      adjustmentType: typeFilter || undefined,
      search: search || undefined,
    });

  useEffect(() => {
    if (canRead) void refresh();
  }, [canRead]);

  if (!canRead) {
    return (
      <div className="p-6 text-sm text-gray-600">
        您没有查看财务调整请求的权限。
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-xl font-semibold">退款 / 补差价 / 转项目</h1>
        {canCreate && (
          <button
            onClick={() => setCreateOpen(true)}
            className="rounded bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground"
          >
            新建调整请求
          </button>
        )}
      </div>

      <div className="flex flex-wrap items-end gap-3 rounded border border-gray-200 bg-gray-50 p-3">
        <div>
          <label className="block text-xs font-medium text-gray-600">状态</label>
          <select
            className="mt-1 rounded border border-gray-300 px-2 py-1 text-sm"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="">全部</option>
            {Object.entries(STATUS_LABELS).map(([k, v]) => (
              <option key={k} value={k}>
                {v}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600">类型</label>
          <select
            className="mt-1 rounded border border-gray-300 px-2 py-1 text-sm"
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
          >
            <option value="">全部</option>
            {Object.entries(TYPE_LABELS).map(([k, v]) => (
              <option key={k} value={k}>
                {v}
              </option>
            ))}
          </select>
        </div>
        <div className="flex-1 min-w-[200px]">
          <label className="block text-xs font-medium text-gray-600">搜索</label>
          <input
            className="mt-1 w-full rounded border border-gray-300 px-2 py-1 text-sm"
            placeholder="单号或标题"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <button
          onClick={() => void refresh()}
          className="rounded border border-gray-300 bg-white px-3 py-1 text-sm"
        >
          {loading ? '加载中…' : '查询'}
        </button>
      </div>

      {error && (
        <div className="rounded bg-red-50 px-3 py-2 text-sm text-red-700">
          {error.message}
        </div>
      )}

      <div className="overflow-x-auto rounded border border-gray-200">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50 text-gray-600">
            <tr>
              <th className="px-3 py-2 text-left">单号</th>
              <th className="px-3 py-2 text-left">类型</th>
              <th className="px-3 py-2 text-left">客户</th>
              <th className="px-3 py-2 text-left">订单</th>
              <th className="px-3 py-2 text-left">标题</th>
              <th className="px-3 py-2 text-right">金额</th>
              <th className="px-3 py-2 text-left">状态</th>
              <th className="px-3 py-2 text-left">更新时间</th>
              <th className="px-3 py-2 text-left">操作</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {(data?.rows ?? []).map((row: FinanceAdjustmentSummaryDto) => (
              <tr key={row.id}>
                <td className="px-3 py-2 font-mono text-xs">{row.adjustmentNo}</td>
                <td className="px-3 py-2">
                  {TYPE_LABELS[row.adjustmentType] ?? row.adjustmentType}
                </td>
                <td className="px-3 py-2 font-mono text-xs">{row.customerId}</td>
                <td className="px-3 py-2 font-mono text-xs">{row.orderId ?? '—'}</td>
                <td className="px-3 py-2">{row.title}</td>
                <td className="px-3 py-2 text-right">{fmtCny(row.amount)}</td>
                <td className="px-3 py-2">
                  <StatusBadge status={row.status} />
                </td>
                <td className="px-3 py-2 text-xs text-gray-500">
                  {new Date(row.updatedAt).toLocaleString()}
                </td>
                <td className="px-3 py-2">
                  <button
                    onClick={() => setDetailId(row.id)}
                    className="rounded border border-gray-300 bg-white px-2 py-0.5 text-xs"
                  >
                    详情
                  </button>
                </td>
              </tr>
            ))}
            {(data?.rows ?? []).length === 0 && !loading && (
              <tr>
                <td colSpan={9} className="px-3 py-6 text-center text-gray-500">
                  暂无调整请求
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <CreateAdjustmentDialog
        key={createOpen ? 'open' : 'closed'}
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={() => void refresh()}
      />

      <DetailPanel
        key={detailId ?? 'none'}
        id={detailId}
        onChanged={() => void refresh()}
        onClose={() => setDetailId(null)}
        permissions={permissions}
      />
    </div>
  );
}
